import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Zap, Timer, Trophy, RotateCcw, Calculator } from 'lucide-react';

const MathGame = () => {
  const { t } = useTranslation();
  const [isPlaying, setIsPlaying] = useState(false);
  const [score, setScore] = useState(0);
  const [highScore, setHighScore] = useState(0);
  const [time, setTime] = useState(0);
  const [gameOver, setGameOver] = useState(false);
  const [level, setLevel] = useState(1);
  const [question, setQuestion] = useState('');
  const [answer, setAnswer] = useState('');
  const [correctAnswer, setCorrectAnswer] = useState(0);
  const [streak, setStreak] = useState(0);
  const [questionsAnswered, setQuestionsAnswered] = useState(0);

  const startGame = () => {
    setIsPlaying(true);
    setGameOver(false);
    setScore(0);
    setTime(0);
    setLevel(1);
    setStreak(0);
    setQuestionsAnswered(0);
    setAnswer('');
    generateQuestion();
  };

  const resetGame = () => {
    setIsPlaying(false);
    setGameOver(false);
    setScore(0);
    setTime(0);
    setLevel(1);
    setStreak(0);
    setQuestionsAnswered(0);
    setQuestion('');
    setAnswer('');
    setCorrectAnswer(0);
  };

  const generateQuestion = () => {
    let num1, num2, operation, result;
    
    switch (level) {
      case 1: // Addition
        num1 = Math.floor(Math.random() * 20) + 1;
        num2 = Math.floor(Math.random() * 20) + 1;
        operation = '+';
        result = num1 + num2;
        break;
      case 2: // Subtraction
        num1 = Math.floor(Math.random() * 30) + 10;
        num2 = Math.floor(Math.random() * num1) + 1;
        operation = '-';
        result = num1 - num2;
        break;
      case 3: // Multiplication
        num1 = Math.floor(Math.random() * 12) + 1;
        num2 = Math.floor(Math.random() * 12) + 1;
        operation = '×';
        result = num1 * num2;
        break;
      case 4: // Division
        num2 = Math.floor(Math.random() * 10) + 2;
        result = Math.floor(Math.random() * 10) + 1;
        num1 = num2 * result;
        operation = '÷';
        break;
      default: // Mixed operations
        const operations = ['+', '-', '×'];
        const op = operations[Math.floor(Math.random() * operations.length)];
        switch (op) {
          case '+':
            num1 = Math.floor(Math.random() * 50) + 1;
            num2 = Math.floor(Math.random() * 50) + 1;
            result = num1 + num2;
            break;
          case '-':
            num1 = Math.floor(Math.random() * 50) + 25;
            num2 = Math.floor(Math.random() * num1) + 1;
            result = num1 - num2;
            break;
          case '×':
            num1 = Math.floor(Math.random() * 15) + 1;
            num2 = Math.floor(Math.random() * 15) + 1;
            result = num1 * num2;
            break;
        }
        operation = op;
    }
    
    setQuestion(`${num1} ${operation} ${num2} = ?`);
    setCorrectAnswer(result);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!isPlaying || gameOver) return;

    const userAnswer = parseInt(answer);
    if (isNaN(userAnswer)) return;

    if (userAnswer === correctAnswer) {
      const points = 10 + (streak * 2);
      setScore(prev => prev + points);
      setStreak(prev => prev + 1);
      
      // Level up every 10 correct answers
      if ((questionsAnswered + 1) % 10 === 0) {
        setLevel(prev => Math.min(5, prev + 1));
      }
    } else {
      setStreak(0);
      setScore(prev => Math.max(0, prev - 5));
    }

    setQuestionsAnswered(prev => prev + 1);
    setAnswer('');
    generateQuestion();
  };

  useEffect(() => {
    let interval;
    if (isPlaying && !gameOver) {
      interval = setInterval(() => {
        setTime(prev => prev + 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isPlaying, gameOver]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const accuracy = questionsAnswered > 0 ? Math.round(((questionsAnswered - streak) / questionsAnswered) * 100) : 0;

  return (
    <div className="max-w-4xl mx-auto">
      {/* Game Header */}
      <div className="cyber-card p-6 mb-8">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-center">
          <div className="flex items-center justify-center space-x-2">
            <Zap className="h-6 w-6 text-neon-blue" />
            <div>
              <p className="text-sm text-gray-400 font-tech">{t('game.score')}</p>
              <p className="text-2xl font-cyber font-bold text-neon-blue">{score}</p>
            </div>
          </div>
          
          <div className="flex items-center justify-center space-x-2">
            <Trophy className="h-6 w-6 text-neon-green" />
            <div>
              <p className="text-sm text-gray-400 font-tech">High Score</p>
              <p className="text-2xl font-cyber font-bold text-neon-green">{highScore}</p>
            </div>
          </div>
          
          <div className="flex items-center justify-center space-x-2">
            <Timer className="h-6 w-6 text-neon-pink" />
            <div>
              <p className="text-sm text-gray-400 font-tech">{t('game.time')}</p>
              <p className="text-2xl font-cyber font-bold text-neon-pink">{formatTime(time)}</p>
            </div>
          </div>
          
          <div className="flex items-center justify-center space-x-2">
            <Calculator className="h-6 w-6 text-neon-purple" />
            <div>
              <p className="text-sm text-gray-400 font-tech">{t('game.level')}</p>
              <p className="text-2xl font-cyber font-bold text-neon-purple">{level}</p>
            </div>
          </div>
          
          <div className="flex items-center justify-center space-x-2">
            <RotateCcw className="h-6 w-6 text-neon-yellow" />
            <button
              onClick={resetGame}
              className="cyber-button text-sm px-4 py-2"
            >
              Reset
            </button>
          </div>
        </div>
      </div>

      {/* Game Instructions */}
      {!isPlaying && !gameOver && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="cyber-card p-6 mb-8 text-center"
        >
          <h3 className="text-2xl font-cyber font-bold text-white mb-4">
            Math Challenge
          </h3>
          <p className="text-gray-300 font-tech mb-6">
            Solve math problems quickly and accurately. Build streaks for bonus points!
          </p>
          <button
            onClick={startGame}
            className="cyber-button text-lg px-8 py-3"
          >
            Start Game
          </button>
        </motion.div>
      )}

      {/* Game Area */}
      {isPlaying && !gameOver && (
        <div className="cyber-card p-6 mb-8">
          <div className="text-center mb-6">
            <h4 className="text-xl font-cyber font-bold text-white mb-4">
              Solve this problem:
            </h4>
            
            {/* Question Display */}
            <div className="text-6xl font-cyber font-bold text-neon-blue mb-8">
              {question}
            </div>
            
            {/* Answer Input */}
            <form onSubmit={handleSubmit} className="flex justify-center">
              <input
                type="number"
                value={answer}
                onChange={(e) => setAnswer(e.target.value)}
                className="cyber-button text-2xl text-center w-32 h-16 mr-4"
                placeholder="?"
                autoFocus
              />
              <button
                type="submit"
                className="cyber-button text-lg px-8 py-4"
              >
                Submit
              </button>
            </form>
            
            {/* Streak Display */}
            <div className="mt-6">
              <p className="text-gray-400 font-tech">Current Streak:</p>
              <p className="text-2xl font-cyber font-bold text-neon-green">{streak}</p>
            </div>
          </div>
        </div>
      )}

      {/* Game Stats */}
      {isPlaying && (
        <div className="cyber-card p-6 mb-8">
          <div className="text-center">
            <h4 className="text-xl font-cyber font-bold text-white mb-4">
              Game Statistics
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="text-center">
                <p className="text-gray-400 font-tech">Questions</p>
                <p className="text-2xl font-cyber font-bold text-neon-blue">{questionsAnswered}</p>
              </div>
              <div className="text-center">
                <p className="text-gray-400 font-tech">Streak</p>
                <p className="text-2xl font-cyber font-bold text-neon-green">{streak}</p>
              </div>
              <div className="text-center">
                <p className="text-gray-400 font-tech">Level</p>
                <p className="text-2xl font-cyber font-bold text-neon-pink">{level}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Game Over Message */}
      {gameOver && (
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          className="cyber-card p-6 mb-8 text-center border-neon-red/50"
        >
          <h3 className="text-3xl font-cyber font-bold text-neon-red mb-4">
            Game Over!
          </h3>
          <p className="text-gray-300 font-tech mb-4">
            Final Score: {score} | Questions: {questionsAnswered} | Time: {formatTime(time)}
          </p>
          <div className="flex justify-center space-x-4">
            <button
              onClick={startGame}
              className="cyber-button text-lg px-6 py-3"
            >
              Play Again
            </button>
            <button
              onClick={resetGame}
              className="cyber-button text-lg px-6 py-3 border-neon-red text-neon-red hover:bg-neon-red hover:text-dark-300"
            >
              Reset
            </button>
          </div>
        </motion.div>
      )}

      {/* Instructions */}
      <div className="cyber-card p-6">
        <div className="text-center">
          <h4 className="text-xl font-cyber font-bold text-white mb-4">
            How to Play
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-gray-300 font-tech">
            <div>
              <p className="font-bold text-neon-blue mb-2">Level 1</p>
              <p>Addition problems</p>
            </div>
            <div>
              <p className="font-bold text-neon-green mb-2">Level 2</p>
              <p>Subtraction problems</p>
            </div>
            <div>
              <p className="font-bold text-neon-pink mb-2">Level 3</p>
              <p>Multiplication problems</p>
            </div>
            <div>
              <p className="font-bold text-neon-yellow mb-2">Level 4+</p>
              <p>Division and mixed operations</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MathGame;
